import { FunctionComponent, useMemo } from 'react';
import { Ticket } from '../../model/ticket.types';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from '@mui/material';
import {
    usePostTicketCancelInsuranceMutation,
    usePostTicketRevokeInsuranceMutation,
    usePostTicketCancelInsuranceFromInsuranceMutation,
} from '../../api/ticket.api';

import { useNotification } from '@starjack/features/notifications';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';

type InsuranceCancelDialogProps = {
    open: boolean;
    onClose: () => void;
    ticket: Ticket;
    type: 'cancel' | 'refund' | 'cancelFromInsurance';
};

export const InsuranceCancelDialog: FunctionComponent<
    InsuranceCancelDialogProps
> = ({ open, onClose, ticket, type }) => {
    const [postCancelInsurance, { isLoading: cancelLoading }] =
        usePostTicketCancelInsuranceMutation();
    const [postRevokeInsurance, { isLoading: revokeLoading }] =
        usePostTicketRevokeInsuranceMutation();
    const [postCancelFromInsurance, { isLoading: cancelInsuranceLoading }] =
        usePostTicketCancelInsuranceFromInsuranceMutation();
    const { addNotification } = useNotification();
    const { t } = useTranslation();
    const dialogTitle = useMemo(() => {
        if (type === 'cancel') {
            return 'Versicherung stornieren';
        } else if (type === 'refund') {
            return 'Versicherung widerrufen';
        } else if (type === 'cancelFromInsurance') {
            return 'Stornierung durch Versicherer';
        }
    }, [type]);

    const dialogBody = useMemo(() => {
        if (type === 'cancel') {
            return 'Soll die Versicherung wirklich storniert werden?';
        } else if (type === 'refund') {
            return 'Soll die Versicherung wirklich widerrufen werden?';
        } else if (type === 'cancelFromInsurance') {
            return 'Soll die Versicherung über den Versicherer storniert werden?';
        }
    }, [type]);

    const cancelButtonLabel = useMemo(() => {
        if (type === 'cancel') {
            return 'Stornieren';
        } else if (type === 'refund') {
            return 'Widerrufen';
        } else if (type === 'cancelFromInsurance') {
            return 'Stornierung durch Versicherer';
        }
    }, [type]);
    const cancelAction = useMemo(() => {
        if (type === 'cancel') {
            return postCancelInsurance;
        } else if (type === 'refund') {
            return postRevokeInsurance;
        } else {
            return postCancelFromInsurance;
        }
    }, [type]);
    const successMessage = useMemo(() => {
        if (type === 'cancel') {
            return 'Versicherung wurde storniert';
        } else if (type === 'refund') {
            return 'Versicherung wurde widerrufen';
        } else {
            return 'Versicherung wurde storniert';
        }
    }, [type]);
    const errorMessage = useMemo(() => {
        if (type === 'cancel') {
            return 'Versicherung konnte nicht storniert werden';
        } else if (type === 'refund') {
            return 'Versicherung konnte nicht widerrufen werden';
        } else {
            return 'Versicherung konnte nicht storniert werden';
        }
    }, [type]);

    const onCancel = () => {
        cancelAction({
            ticketId: ticket.id,
        })
            .unwrap()
            .then(() => {
                addNotification({
                    message: successMessage,
                    type: 'success',
                });
            })
            .catch(() => {
                addNotification({
                    message: errorMessage,
                    type: 'error',
                });
            });
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>{dialogTitle}</DialogTitle>
            <DialogContent>
                <DialogContentText>{dialogBody}</DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('dialogActions.cancel')}</Button>
                <LoadingButton
                    loading={
                        cancelLoading || revokeLoading || cancelInsuranceLoading
                    }
                    variant='contained'
                    color='error'
                    onClick={onCancel}
                >
                    {cancelButtonLabel}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};
